<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  FunnelIcon,
} from '@heroicons/vue/24/outline'
import { useOmnisearchStore } from '@/stores/useOmnisearchStore'
import { useOmnisearch, isQueryLongEnough, MIN_QUERY_LENGTH } from '@/v2/composables/useOmnisearch'
import OsSection from '@/v2/components/omnisearch/OsSection.vue'
import OsCompanyResult from '@/v2/components/omnisearch/results/OsCompanyResult.vue'
import OsContactResult from '@/v2/components/omnisearch/results/OsContactResult.vue'
import OsOrderResult from '@/v2/components/omnisearch/results/OsOrderResult.vue'
import OsQuoteResult from '@/v2/components/omnisearch/results/OsQuoteResult.vue'
import OsInvoiceResult from '@/v2/components/omnisearch/results/OsInvoiceResult.vue'
import OsServiceResult from '@/v2/components/omnisearch/results/OsServiceResult.vue'
import OsLeadResult from '@/v2/components/omnisearch/results/OsLeadResult.vue'
import OsTicketResult from '@/v2/components/omnisearch/results/OsTicketResult.vue'
import OsCampaignResult from '@/v2/components/omnisearch/results/OsCampaignResult.vue'
import OsEligibilityResult from '@/v2/components/omnisearch/results/OsEligibilityResult.vue'
import OsOpportunityResult from '@/v2/components/omnisearch/results/OsOpportunityResult.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import type { OmnisearchEntityKey, TypedSection } from '@/api/omnisearchApi'

const route = useRoute()
const router = useRouter()
const store = useOmnisearchStore()
useOmnisearch('page')

const inputRef = ref<HTMLInputElement | null>(null)

// ── Filtres de scope ───────────────────────────────────────────────────────────
// Tickets visible pour tous : GET /tickets est scopé côté back sur le demandeur courant
const SCOPE_OPTIONS: { key: OmnisearchEntityKey | 'all'; label: string }[] = [
  { key: 'all', label: 'Tout' },
  { key: 'companies', label: 'Entreprises' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'orders', label: 'Commandes' },
  { key: 'quotes', label: 'Devis' },
  { key: 'leads', label: 'Leads' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'invoices', label: 'Factures' },
  { key: 'services', label: 'Services' },
  { key: 'campaigns', label: 'Campagnes' },
  { key: 'eligibilities', label: 'Éligibilités' },
  { key: 'opportunities', label: 'Affaires' },
]

const activeScope = ref<OmnisearchEntityKey | 'all'>('all')

function setScope(key: OmnisearchEntityKey | 'all') {
  activeScope.value = key
  router.replace({ query: { ...route.query, scope: key === 'all' ? undefined : key } })
}

// ── Sync URL ↔ store ──────────────────────────────────────────────────────────
onMounted(() => {
  const q = route.query.q as string | undefined
  const scope = route.query.scope as OmnisearchEntityKey | undefined
  if (q) store.setQuery(q)
  if (scope) activeScope.value = scope
  // Focus auto
  inputRef.value?.focus()
})

watch(
  () => store.query,
  (q) => {
    router.replace({ query: { ...route.query, q: q || undefined } })
  },
)

// ── Sections filtrées ──────────────────────────────────────────────────────────
const allSections = computed((): TypedSection[] => {
  const r = store.results
  if (!r) return []
  const all: TypedSection[] = [
    { key: 'companies',     label: 'Entreprises',  items: r.companies     },
    { key: 'contacts',      label: 'Contacts',     items: r.contacts      },
    { key: 'orders',        label: 'Commandes',    items: r.orders        },
    { key: 'quotes',        label: 'Devis',        items: r.quotes        },
    { key: 'leads',         label: 'Leads',        items: r.leads         },
    { key: 'tickets',       label: 'Tickets',      items: r.tickets       },
    { key: 'invoices',      label: 'Factures',     items: r.invoices      },
    { key: 'services',      label: 'Services',     items: r.services      },
    { key: 'campaigns',     label: 'Campagnes',    items: r.campaigns     },
    { key: 'eligibilities', label: 'Éligibilités', items: r.eligibilities },
    { key: 'opportunities', label: 'Affaires',     items: r.opportunities },
  ]
  return all
})

const visibleSections = computed(() => {
  if (activeScope.value === 'all') return allSections.value.filter((s) => s.items.length > 0)
  return allSections.value.filter((s) => s.key === activeScope.value && s.items.length > 0)
})

const countByScope = computed<Record<string, number>>(() => {
  const r = store.results
  if (!r) return {} as Record<string, number>
  return {
    all: Object.values(r).reduce((s, a) => s + a.length, 0),
    companies: r.companies.length,
    contacts: r.contacts.length,
    orders: r.orders.length,
    quotes: r.quotes.length,
    leads: r.leads.length,
    tickets: r.tickets.length,
    invoices: r.invoices.length,
    services: r.services.length,
    campaigns: r.campaigns.length,
    eligibilities: r.eligibilities.length,
    opportunities: r.opportunities.length,
  }
})

function onInput(e: Event) {
  store.setQuery((e.target as HTMLInputElement).value)
}

function clearQuery() {
  store.setQuery('')
  inputRef.value?.focus()
}

function onNavigate() {
  store.pushHistory(store.query)
}

// Drill-down par entité : les sections avec une page liste V2 dédiée (clients,
// contacts) plafonnées renvoient vers cette page pour voir la suite des résultats.
function drilldownPath(key: OmnisearchEntityKey): string | undefined {
  if (key === 'companies') return '/clients'
  if (key === 'contacts') return '/contacts'
  return undefined
}
function goToDrilldown(key: OmnisearchEntityKey) {
  const path = drilldownPath(key)
  if (!path) return
  store.pushHistory(store.query)
  router.push({ path, query: { q: store.query } })
}

const showEmpty = computed(
  () =>
    isQueryLongEnough(store.query) &&
    !store.isLoading &&
    !store.hasResults &&
    !store.hasError,
)
</script>

<template>
  <div class="w-full px-4 py-8">
    <!-- Titre -->
    <div class="mb-6">
      <div class="flex items-center gap-1">
        <h1 class="text-2xl font-bold text-base-content flex items-center gap-2">
          <MagnifyingGlassIcon class="w-6 h-6 text-primary" />
          Recherche globale
        </h1>
        <HelpButton />
      </div>
      <p class="text-sm text-base-content/50 mt-1">
        Recherchez simultanément dans toutes les entités
      </p>
    </div>

    <!-- Barre de recherche -->
    <div class="flex items-center mx-auto w-full md:w-2/3 gap-3 px-4 py-3 bg-base-100 rounded-xl border border-base-300 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all mb-6">
      <MagnifyingGlassIcon
        class="w-5 h-5 flex-shrink-0 transition-colors"
        :class="store.isLoading ? 'text-primary' : 'text-base-content/40'"
      />
      <input
        ref="inputRef"
        type="text"
        placeholder="Nom d'entreprise, contact, référence commande, SIRET…"
        class="flex-1 bg-transparent outline-none text-base placeholder:text-base-content/30 text-base-content"
        :value="store.query"
        @input="onInput"
      />
      <div class="flex items-center gap-2 flex-shrink-0">
        <ArrowPathIcon v-if="store.isLoading" class="w-4 h-4 text-primary animate-spin" />
        <button
          v-if="store.query"
          class="w-7 h-7 rounded-full flex items-center justify-center hover:bg-base-200 text-base-content/40 hover:text-base-content transition-colors"
          @click="clearQuery"
        >
          <XMarkIcon class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Filtres de scope -->
    <div
      v-if="store.results || store.isLoading"
      class="flex items-center gap-1.5 mb-6 flex-wrap"
    >
      <FunnelIcon class="w-4 h-4 text-base-content/40 flex-shrink-0 mr-1" />
      <button
        v-for="opt in SCOPE_OPTIONS"
        :key="opt.key"
        class="px-3 py-1 rounded-full text-sm transition-colors font-medium border"
        :class="
          activeScope === opt.key
            ? 'bg-primary text-primary-content border-primary'
            : 'bg-base-100 text-base-content/60 border-base-300 hover:border-primary/50 hover:text-base-content'
        "
        @click="setScope(opt.key)"
      >
        {{ opt.label }}
        <span
          v-if="store.results && countByScope[opt.key]"
          class="ml-1 text-xs opacity-70"
        >
          {{ countByScope[opt.key] }}
        </span>
      </button>
    </div>

    <!-- État vide initial -->
    <div v-if="!store.query" class="py-16 text-center">
      <MagnifyingGlassIcon class="w-12 h-12 mx-auto text-base-content/15 mb-4" />
      <p class="text-base-content/40 text-sm">Commencez à taper pour lancer la recherche</p>
      <div v-if="store.recentSearches.length" class="mt-6">
        <p class="text-xs text-base-content/40 mb-3 font-medium uppercase tracking-wider">Recherches récentes</p>
        <div class="flex flex-wrap gap-2 justify-center">
          <button
            v-for="term in store.recentSearches"
            :key="term"
            class="px-4 py-1.5 text-sm bg-base-100 hover:bg-base-200 rounded-full border border-base-300 transition-colors text-base-content/70"
            @click="store.setQuery(term)"
          >
            {{ term }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-else-if="!isQueryLongEnough(store.query)"
      class="py-12 text-center text-sm text-base-content/40"
    >
      Saisissez au moins {{ MIN_QUERY_LENGTH }} caractères pour lancer la recherche…
    </div>

    <div v-else-if="store.isLoading && !store.results" class="py-16 flex flex-col items-center gap-4">
      <ArrowPathIcon class="w-8 h-8 animate-spin text-primary" />
      <p class="text-sm text-base-content/50">Recherche en cours dans toutes les entités…</p>
    </div>

    <div v-else-if="store.hasError" class="py-12 text-center">
      <p class="text-error text-sm font-medium">Une erreur est survenue lors de la recherche.</p>
      <button class="btn btn-sm btn-ghost mt-3" @click="store.setQuery(store.query)">
        Réessayer
      </button>
    </div>

    <div v-else-if="showEmpty" class="py-16 text-center">
      <MagnifyingGlassIcon class="w-10 h-10 mx-auto text-base-content/15 mb-3" />
      <p class="text-base-content/50 font-medium">Aucun résultat pour « {{ store.query }} »</p>
      <p class="text-xs text-base-content/30 mt-2">Vérifiez l'orthographe ou essayez un autre terme</p>
    </div>

    <!-- Résultats -->
    <template v-else-if="store.results">
      <div
        v-if="visibleSections.length === 0 && activeScope !== 'all'"
        class="py-10 text-center text-sm text-base-content/40"
      >
        Aucun résultat dans cette catégorie pour « {{ store.query }} »
      </div>

      <div v-else class="space-y-4">
        <div
          v-for="section in visibleSections"
          :key="section.key"
          class="bg-base-100 rounded-xl border border-base-200 overflow-hidden shadow-sm"
        >
          <OsSection
            :label="section.label"
            :count="section.items.length"
            :show-all="!!drilldownPath(section.key) && section.items.length >= 20"
            @show-all="goToDrilldown(section.key)"
          />
          <div class="px-1 pb-1">
            <template v-if="section.key === 'companies'">
              <OsCompanyResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
            <template v-else-if="section.key === 'contacts'">
              <OsContactResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
            <template v-else-if="section.key === 'orders'">
              <OsOrderResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
            <template v-else-if="section.key === 'quotes'">
              <OsQuoteResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
              />
            </template>
            <template v-else-if="section.key === 'invoices'">
              <OsInvoiceResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
              />
            </template>
            <template v-else-if="section.key === 'services'">
              <OsServiceResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
              />
            </template>
            <template v-else-if="section.key === 'leads'">
              <OsLeadResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
            <template v-else-if="section.key === 'tickets'">
              <OsTicketResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
            <template v-else-if="section.key === 'campaigns'">
              <OsCampaignResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
            <template v-else-if="section.key === 'eligibilities'">
              <OsEligibilityResult
                v-for="item in section.items"
                :key="item.uuid"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
            <template v-else-if="section.key === 'opportunities'">
              <OsOpportunityResult
                v-for="item in section.items"
                :key="item.id"
                :item="item"
                :query="store.query"
                @navigate="onNavigate"
              />
            </template>
          </div>
        </div>

        <p class="text-xs text-center text-base-content/30 pb-4">
          {{ store.totalResults }} résultat{{ store.totalResults > 1 ? 's' : '' }} — max 20 par catégorie
        </p>
      </div>
    </template>
  </div>
</template>
